import React, { useEffect, useState } from "react";
import { Modal } from "../../components/Modal/Modal";
import {
  ViewerOrganizations,
  useViewer,
  useUpdateOrganizationPoTemplate,
} from "../../schema";
import { Button } from "../../components/Button/Button";
import { Field } from "../../components/Field/Field";
import { Form } from "../../components/Form/Form";
import { useForm } from "../../lib/react-apollo-hooks-form";
import { ErrorView } from "../ErrorView/ErrorView";
import { LoadingView } from "../LoadingView/LoadingView";

export interface UpdatePoTemplateProps {
  activeOrganization: ViewerOrganizations;
  onModalClose(): void;
}

export const UpdatePoTemplate: React.FC<UpdatePoTemplateProps> = ({
  activeOrganization,
  onModalClose,
}) => {
  // load active organization
  const { loading: isFetching, error: fetchError } = useViewer({
    fetchPolicy: "network-only",
    variables: {
      organizationId: activeOrganization.id,
    },
  });

  // setup form
  const {
    useInput,
    loading: isSaving,
    error: saveError,
    submit: updateOrganizationPoTemplate,
  } = useForm({
    mutation: useUpdateOrganizationPoTemplate,
    options: { refetchQueries: ["Viewer"] },
    onSuccess: () => onModalClose(),
  });

  // setup state
  const [isInitialized, setIsInitialized] = useState(false);

  // configure inputs
  const primaryColorInput = useInput({ name: "primaryColor", optional: true });
  const secondaryColorInput = useInput({
    name: "secondaryColor",
    optional: true,
  });
  const noteInput = useInput({ name: "note", optional: true });

  useEffect(() => {
    if (!activeOrganization) {
      return;
    }

    if (activeOrganization.modules?.PO_TEMPLATE.primaryColor) {
      primaryColorInput.setValue(
        activeOrganization.modules.PO_TEMPLATE.primaryColor,
      );
    }

    if (activeOrganization.modules?.PO_TEMPLATE.secondaryColor) {
      secondaryColorInput.setValue(
        activeOrganization.modules.PO_TEMPLATE.secondaryColor,
      );
    }

    if (activeOrganization.modules?.PO_TEMPLATE.note) {
      noteInput.setValue(activeOrganization.modules.PO_TEMPLATE.note);
    }

    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganization]);

  // handle loading error
  if (fetchError) {
    return (
      <ErrorView
        title="Loading organization failed"
        error={fetchError}
        modal={{ title: "Update po template", onModalClose }}
      />
    );
  }

  // handle saving error
  if (saveError) {
    return (
      <ErrorView
        title="Updating po template failed"
        error={saveError}
        modal={{ title: activeOrganization.companyName, onModalClose }}
      />
    );
  }

  // handle loading
  if (isFetching || !activeOrganization || !isInitialized) {
    return <LoadingView overlay />;
  }

  // submits the form
  const submit = () => {
    updateOrganizationPoTemplate({
      organizationId: activeOrganization.id,
    });
  };

  return (
    <Modal
      title="PO template"
      onCloseRequested={onModalClose}
      footer={
        <>
          <Button
            data-testid="2b5f4623f4"
            secondary
            onClick={() => onModalClose()}
          >
            Cancel
          </Button>
          <Button
            data-testid="0e83d4e80f"
            loading={isSaving}
            onClick={() => submit()}
          >
            Save
          </Button>
        </>
      }
    >
      <Form onSubmit={submit}>
        <Field
          {...primaryColorInput}
          short
          autofocus
          label="Primary color (Hex)"
        />
        <Field
          {...secondaryColorInput}
          short
          autofocus
          label="Secondary color (Hex)"
        />
        <Field {...noteInput} short autofocus label="Note" />
      </Form>
    </Modal>
  );
};
